/**
 * SchedulePublicView — helskärm för körschema.
 * Visar hela schemat med aktuellt block markerat i orange.
 * Lyssnar på currentBlock via onSnapshot — uppdateras i realtid.
 * Kräver ingen inloggning.
 */
import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { db } from '../../../../firebase/config';
import { doc, onSnapshot } from 'firebase/firestore';
import { useCompanyLogo } from '../../../../hooks/useQRLogo';

const accent = '#E05500';

export default function SchedulePublicView({ sessionId }) {
  const [session, setSession] = useState(undefined);
  const companyLogo = useCompanyLogo();

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'workshops', sessionId), snap => {
      setSession(snap.exists() ? snap.data() : null);
    });
    return unsub;
  }, [sessionId]);

  const schedule    = session?.schedule;
  const blocks      = schedule?.blocks || [];
  const current     = schedule?.currentBlock ?? 0;
  const wifiSsid    = session?.wifiSsid || '';
  const wifiPassword= session?.wifiPassword || '';

  return (
    <Box sx={{
      minHeight: '100vh',
      height: '100%',
      bgcolor: '#1a1a1a',
      display: 'flex',
      flexDirection: 'column',
      px: { xs: 4, md: 8 },
      py: { xs: 4, md: 6 },
    }}>

      {/* Loading */}
      {session === undefined && (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress sx={{ color: accent }} />
        </Box>
      )}

      {/* No schedule */}
      {session !== undefined && !schedule && (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{ color: '#444' }}>Inget körschema är skapat ännu.</Typography>
        </Box>
      )}

      {schedule && (
        <>
          {/* Header */}
          <Box sx={{ mb: { xs: 3, md: 5 } }}>
            <Typography sx={{ color: '#555', fontSize: 'clamp(0.65rem, 1.2vw, 0.9rem)', fontWeight: 700, letterSpacing: 2, mb: 0.5 }}>
              KÖRSCHEMA
            </Typography>
            <Typography sx={{ color: '#fff', fontSize: 'clamp(1.1rem, 2.5vw, 2rem)', fontWeight: 600 }}>
              {session?.title || ''}
            </Typography>
          </Box>

          {/* Block list */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1, md: 1.5 }, flex: 1 }}>
            {blocks.map((block, i) => {
              const isCurrent = i === current;
              const isPast    = i < current;
              return (
                <Box
                  key={block.id || i}
                  sx={{
                    borderRadius: 1.5,
                    bgcolor: isCurrent ? accent + '18' : 'transparent',
                    border: '1px solid',
                    borderColor: isCurrent ? accent + '66' : 'transparent',
                    borderLeft: `3px solid ${isCurrent ? accent : isPast ? '#333' : '#2a2a2a'}`,
                    transition: 'all 0.4s ease',
                    opacity: isPast ? 0.4 : 1,
                    px: { xs: 2, md: 3 },
                    py: { xs: 1.25, md: 1.75 },
                  }}
                >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, md: 3 } }}>
                  {/* Block number */}
                  <Box sx={{
                    width: { xs: 28, md: 36 },
                    height: { xs: 28, md: 36 },
                    borderRadius: '50%',
                    bgcolor: isCurrent ? accent : '#2a2a2a',
                    border: `1px solid ${isCurrent ? accent : '#333'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, transition: 'all 0.4s',
                  }}>
                    <Typography sx={{ color: isCurrent ? '#fff' : '#555', fontWeight: 800, fontSize: { xs: '0.7rem', md: '0.82rem' } }}>
                      {i + 1}
                    </Typography>
                  </Box>

                  {/* Time */}
                  <Typography sx={{
                    color: isCurrent ? accent : '#555',
                    fontWeight: 700,
                    fontSize: { xs: 'clamp(0.7rem, 1.5vw, 1rem)', md: 'clamp(0.8rem, 1.5vw, 1.1rem)' },
                    minWidth: { xs: 90, md: 130 },
                    flexShrink: 0,
                    fontFamily: 'monospace',
                    transition: 'color 0.4s',
                  }}>
                    {block.time}
                  </Typography>

                  {/* Title */}
                  <Typography sx={{
                    color: isCurrent ? '#fff' : '#666',
                    fontWeight: isCurrent ? 600 : 400,
                    fontSize: { xs: 'clamp(0.85rem, 2vw, 1.2rem)', md: 'clamp(1rem, 2vw, 1.4rem)' },
                    flex: 1,
                    transition: 'all 0.4s',
                  }}>
                    {block.title}
                  </Typography>

                  {/* Duration badge */}
                  {block.duration && (
                    <Box sx={{
                      px: { xs: 1, md: 1.5 }, py: 0.3,
                      borderRadius: 100,
                      bgcolor: isCurrent ? accent + '22' : '#222',
                      border: `1px solid ${isCurrent ? accent + '44' : '#333'}`,
                      flexShrink: 0,
                      transition: 'all 0.4s',
                    }}>
                      <Typography sx={{
                        color: isCurrent ? accent : '#444',
                        fontSize: { xs: '0.62rem', md: '0.72rem' },
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                      }}>
                        {block.duration} min
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Sub-items — only show for current block */}
                {isCurrent && block.subItems?.length > 0 && (
                  <Box sx={{
                    mt: 1.25, ml: { xs: 5, md: 7 },
                    pl: { xs: 1.5, md: 2 },
                    borderLeft: `2px solid ${accent}44`,
                    display: 'flex', flexDirection: 'column', gap: 0.6,
                  }}>
                    {block.subItems.map((sub, si) => (
                      <Box key={si} sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, md: 2 } }}>
                        <Typography sx={{
                          color: '#777',
                          fontSize: { xs: '0.7rem', md: '0.78rem' },
                          fontFamily: 'monospace',
                          minWidth: { xs: 40, md: 50 },
                          flexShrink: 0,
                        }}>
                          {sub.duration} min
                        </Typography>
                        <Typography sx={{
                          color: '#aaa',
                          fontSize: { xs: '0.78rem', md: '0.88rem' },
                          flex: 1,
                        }}>
                          {sub.text}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
                </Box>
              );
            })}
          </Box>

          {/* Footer — progress + logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 4, pt: 2, borderTop: '1px solid #222' }}>
            <Box sx={{ display: 'flex', gap: 0.75 }}>
              {blocks.map((_, i) => (
                <Box key={i} sx={{
                  width: i === current ? 20 : 6, height: 6,
                  borderRadius: 3,
                  bgcolor: i < current ? '#333' : i === current ? accent : '#2a2a2a',
                  transition: 'all 0.3s',
                }} />
              ))}
            </Box>
            <Typography sx={{ color: '#333', fontSize: '0.7rem', fontFamily: 'monospace' }}>
              {current + 1} / {blocks.length}
            </Typography>
            <Box sx={{ flex: 1 }} />
            {/* WiFi-info */}
            {wifiSsid && (
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: { xs: 1.5, md: 2 },
                px: { xs: 1.5, md: 2 }, py: { xs: 0.5, md: 0.75 },
                borderRadius: 1.5,
                bgcolor: '#ffffff08',
                border: '1px solid #ffffff14',
              }}>
                <Typography sx={{ color: '#555', fontSize: { xs: '0.58rem', md: '0.65rem' }, fontWeight: 700, letterSpacing: 1 }}>
                  WiFi
                </Typography>
                <Typography sx={{ color: '#aaa', fontSize: { xs: '0.65rem', md: '0.75rem' }, fontWeight: 600 }}>
                  {wifiSsid}
                </Typography>
                {wifiPassword && (
                  <>
                    <Typography sx={{ color: '#333', fontSize: '0.6rem' }}>·</Typography>
                    <Typography sx={{ color: '#777', fontSize: { xs: '0.65rem', md: '0.75rem' }, fontFamily: 'monospace' }}>
                      {wifiPassword}
                    </Typography>
                  </>
                )}
              </Box>
            )}

            {companyLogo && (
              <Box
                component="img"
                src={companyLogo}
                alt=""
                sx={{ height: { xs: 20, md: 28 }, maxWidth: 120, objectFit: 'contain', opacity: 0.5 }}
              />
            )}
          </Box>
        </>
      )}
    </Box>
  );
}
